import { ReactElement, useContext, useEffect, useState } from "react";
import Chart from "../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../components/charts/chartUtils";
import * as d3 from "d3";
import AxisHorizontal from "../../../components/charts/AxisHorizontal";
import {
  IDelayBase, IProduction,
  IProductionExtended,
  IStrandProduction,
  IVariableGroup
} from "../../../types";
import { Paper, useTheme } from "@mui/material";
import KeyValueItem from "../../../components/charts/keyValueItem";
import Detail from "../../../components/dialogs/detail";
import {
  formatTimeWithSeconds, getCurrentDatetimeInSeconds
} from "../../../system/utils";
import { Alert } from "@mui/material";
import {
  xProductionBeginAccessorScaled,
  xProductionDurationAccessorScaled, xProductionEndAccessorScaled,
} from "../utils";
import { SystemContext } from "../../../context/systemContext";
import { getLanguage } from "../configuration/language";
import { getPalette } from "../../../configuration/configuration";
import SkeletonGantt from "./skeletonGantt";
import { Duration } from "luxon";
import ProductionInformation from "../../mill/components/productionInformation";
import DelayLayerGantt from "../../interruptions/compoenents/delayLayerGantt";
import HtmlTooltip from "../../../components/charts/tooltip";
import VariablesLayerGantt from "../variablesLayerGantt";
import SamplesLayerGantt from "./samplesLayerGantt";

interface IHeatChartProps {
  title: string;
  productionData: IProduction[];
  parentSize: {
    width: number;
    height: number;
  } | null;
}

const heatHeight = 22;
const orderHeight = 30;
// const sliderHeight =  30;
const marginTop = 40;
// const shiftHeader = 30;
const marginBottom = 60;


const GanttChartMobileParallelMachine = ({ productionData, title, parentSize }: IHeatChartProps): ReactElement => {
  const chartID = useUniqueId("gantt-chart-mobile");

  let totalHeight = 0;
  productionData.forEach((p) => {
    totalHeight += p.productionMachines.length * heatHeight + orderHeight;
  });

  const theme = useTheme();
  const systemContext = useContext(SystemContext);
  const [openDetail, setOpenDetail] = useState(false);
  const [maximum, setMaximum] = useState(0);
  const [selectedProduction, setProductionItem] = useState<IProductionExtended | null>(null);
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: totalHeight + marginTop + marginBottom,
    marginBottom,
    marginLeft: 40,
    marginRight: 40,
    marginTop
  });


  useEffect(() => {
    const heats = productionData.map(item => item.endDatetime ? item.endDatetime - item.beginDatetime : getCurrentDatetimeInSeconds() - item.beginDatetime);
    setMaximum(Math.max(...heats));
  }, [productionData]);


  if (productionData.length === 0) {
    return (
      <Paper>
        <Alert severity="warning">No Data to Display.</Alert>
      </Paper>
    );
  }

  if (maximum === 0) {
    return <SkeletonGantt/>;
  }


  const xScale = d3.scaleLinear()
    .domain([0, maximum])
    .range([0, dimensions.boundedWidth]);

  const onSelected = (selectedItem: IProductionExtended) => {
    setProductionItem(selectedItem);
    setOpenDetail(true);
  };

  let heatGroup = heatHeight + 27;
  let currentProductHeight = 0;

  return (
    <Paper ref={ref} style={{ height: dimensions.height }} elevation={0}>
      <Chart dimensions={dimensions}>
        <defs>
          <clipPath id="cut-off">
            <rect x="0" y="0" width={dimensions.boundedWidth} height={dimensions.boundedHeight} />
          </clipPath>
        </defs>

        <AxisHorizontal
          chartID={chartID}
          dimensions={dimensions}
          scale={xScale}
          label={title}
          ticks={getTicks(xScale.domain(), parentSize && parentSize.width > 600 ? 5 : 3)}
          formatTick={(value) => Duration.fromMillis(value as number * 1000).toFormat("hh:mm:ss")}
        />

        <g transform={`translate(${0}, ${-20})`}>
          <g transform={`translate(${0}, ${-10})`}>
            <polygon fill={"green"} points="5 0 0 5 5 10 10 5 5 0"/>
          </g>
          <text y={0}
            x={12}
            fill={theme.palette.text.primary}
            fontSize="1em"
            fillOpacity="0.9"
            textAnchor="start">
            {getLanguage(systemContext.languageCode).samples}
          </text>

          <g transform={`translate(${78}, ${-10})`}>
            <polygon fill={"#d84315"} points="0 0 5 10 10 0 0 0"/>
          </g>
          <text y={0}
            x={90}
            fill={theme.palette.text.primary}
            fontSize="1em"
            fillOpacity="0.9"
            textAnchor="start">
            {getLanguage(systemContext.languageCode).variables}
          </text>
        </g>

        <g clipPath="url(#cut-off)">
          {productionData.map((production: IProduction) => {
            if (production.productionMachines.length === 0) {
              return null;
            }

            heatGroup += currentProductHeight;
            currentProductHeight = heatHeight * production.productionMachines.length + orderHeight;

            const lastProductionItem = production.productionMachines[production.productionMachines.length - 1];
            const productionKey = `production-${production.productionID}`;
            const showMachineName = production.productionMachines.length > 1;
            const p1 = xProductionBeginAccessorScaled(production, xScale);
            const p2 = xProductionEndAccessorScaled(production, xScale);
            const flagP = (p2 - p1) / (lastProductionItem.reference ?? lastProductionItem.title).length > 8.3;

            return (
              <g key={productionKey}>
                <line className="Axis__line" strokeWidth="0.5" x1={0} x2={dimensions.boundedWidth} y1={heatGroup - 25} y2={heatGroup - 25} />

                {production.productionMachines.map((productionItem: IProductionExtended, heatIndex: number) => {
                  const heatKey = `${chartID}-heat-${productionItem.title}-${heatIndex}`;
                  const heatY = heatIndex * heatHeight + 5;
                  const heatWidth = xProductionDurationAccessorScaled(productionItem, xScale);

                  const variables: IVariableGroup[] = [];
                  productionItem.variables.forEach(variable => {
                    const item = variables.find(x => x.datetime === variable.datetime);
                    if (!item) {
                      variables.push({
                        datetime: variable.datetime,
                        variables: [ variable ]
                      });
                    } else {
                      item.variables.push(variable);
                    }
                  });

                  const rmData = (productionItem as any).__typename === "StrandProduction" ? productionItem as IStrandProduction : null;

                  const flag = heatWidth / (showMachineName ? productionItem.machine.name : productionItem.reference ?? productionItem.title).length > 8.3;
                  let totalDelays = 0;
                  let barInMill = 0;
                  if(rmData) {
                    rmData.delays.filter(xyz => !xyz.isDownDay).forEach(delay => {
                      totalDelays += delay.duration ?? 0;
                    });

                    barInMill = ((rmData.endDatetime ?? getCurrentDatetimeInSeconds()) - rmData.beginDatetime - totalDelays) / 60;
                  }

                  const xAccessorScaled = (d: IDelayBase) => xScale(d.beginDatetime - productionItem.beginDatetime);
                  const widthAccessorScaled = (d: IDelayBase) => xScale((d.endDatetime ?? productionItem.endDatetime ?? getCurrentDatetimeInSeconds()) - d.beginDatetime);

                  return(
                    <g key={heatKey} transform={`translate(0, ${heatGroup})`}  height={heatHeight} onClick={() => onSelected(productionItem)}>
                      <HtmlTooltip title={
                        <div>
                          <div className="display-flex-column align-items-center" >
                            <strong>Heat</strong>
                            <strong>{productionItem.title}</strong>
                          </div>
                          <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(productionItem.beginDatetime as number)} />
                          <KeyValueItem name={"End"} value={formatTimeWithSeconds(productionItem.endDatetime as number)} />
                          { rmData &&
                          <>
                            <KeyValueItem name={"Bar in Mill"} value={barInMill.toFixed(1)} unit="min"/>
                            <KeyValueItem name={"Heat Number"} value={rmData.heatNumber}/>
                            <KeyValueItem name={"Steel"} value={rmData.steelCode}/>
                          </>
                          }
                        </div>
                      }>
                        <g>
                          <rect
                            cursor="pointer"
                            fill={getPalette().statusColor.production.background}
                            x={0}
                            y={heatY}
                            width={heatWidth}
                            height={17}
                          />
                        </g>
                      </HtmlTooltip>

                      <DelayLayerGantt delays={productionItem.delays} height={17} y={heatY} xAccessorScaled={xAccessorScaled}
                        widthAccessorScaled={widthAccessorScaled} selected={null} chartKey={chartID} />

                      <SamplesLayerGantt chartID={heatKey} title={productionItem.title} samples={productionItem.samples}
                        y={heatY - 13} xAccessorScaled={sample => xScale((sample.sampleDatetime ?? productionItem.beginDatetime) - productionItem.beginDatetime) - 4} />

                      <VariablesLayerGantt chartID={heatKey} title={productionItem.title} variableGroups={variables} y={heatY - 13} xAccessorScaled={variableGroup => xScale(variableGroup.datetime) - 5} />

                      <text
                        y={heatY + 12}
                        x={heatWidth / 2.0}
                        fontSize="1em"
                        textAnchor="middle"
                        fill={flag ? "white" : "#444"}
                        fontWeight={"bold"}
                        onClick={() => onSelected(productionItem)}>
                        {productionItem.machine.name}
                      </text>
                    </g>
                  );
                })}

                <HtmlTooltip title={
                  <div className="display-flex-column align-items-center" >
                    <strong>{lastProductionItem.reference ?? lastProductionItem.title}</strong>
                    <strong style={{ fontSize: "0.9em" }}>{}</strong>
                    <strong style={{ fontSize: "0.9em" }}>{}</strong>
                    <KeyValueItem name="Begin"  value={formatTimeWithSeconds(production.beginDatetime)} />
                    <KeyValueItem name="End"  value={formatTimeWithSeconds(production.endDatetime)} />
                    {production.fields.map((field, index) => {
                      return (
                        <KeyValueItem key={`${lastProductionItem.reference}-${index}`} name={field.name}  value={field.value} unit={field.unit} />
                      );
                    })}
                  </div>
                }>
                  <g>
                    <g transform={`translate(0, ${heatGroup - 20})`}>
                      <polygon fill={"#444"} stroke={"#444"}
                        points={`0 0 0 17 5 15 ${p2 - p1 - 5} 15 ${p2 - p1} 17 ${p2 - p1} 0`}/>
                    </g>
                    <text
                      y={heatGroup - 10}
                      x={(xProductionEndAccessorScaled(production, xScale) - xProductionBeginAccessorScaled(production, xScale)) / 2 }
                      fontSize="1em"
                      textAnchor="middle"
                      fill={flagP ? "white" : "#aaa"}
                      fontWeight={"bold"}>
                      {lastProductionItem.reference ?? lastProductionItem.title}
                    </text>
                  </g>
                </HtmlTooltip>
              </g>
            );
          })
          }
        </g>
      </Chart>
      {openDetail && selectedProduction &&
      <Detail closed={() => setOpenDetail(false)} fullWidth={true} maxWidth={"xl"} title="Detail">
        <ProductionInformation productionItem={selectedProduction as IStrandProduction} height={500}/>
      </Detail>
      }
    </Paper>
  );
};

export default GanttChartMobileParallelMachine;
